import { useNavigate } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

/**
 * Function to check if a navlink is active based on the current path.
 * @param navlinkObj - The navlink object to check if it is active
 * @returns - A boolean indicating if the navlink is active
 */
function isNavlinkActive(navlinkObj: any) {
  const currentPath = location.pathname;

  if (!navlinkObj.route) return false;

  return currentPath.includes(navlinkObj.route);
}

interface Props {
  navLinks: { route: string, text: string }[];
  advancedOptionsNavLinks?: { route: string, text: string }[];
  showNumbering?: boolean;
  headerTitle?: string;
};

function SideNavigationMenu({ navLinks, advancedOptionsNavLinks, showNumbering, headerTitle }: Props) {

  // Hooks
  const navigate = useNavigate();
  const { t } = useTranslation(["components"]);

  /**
   * Navigates to a specified route while preserving existing query parameters.
   * @param {string} route - The base route to navigate to. This should include the path but not the query parameters.
   * @returns {void}
  */
  const handleNavigate = (route: string): void => {
    const url = new URL(window.location.href);
    const params = url.searchParams.toString();

    const fullPath = params ? `${route}?${params}` : route;

    navigate(fullPath);
  };

  return (
    <div className="min-w-3 sm:min-w-[200px]">
      <nav className="sticky top-0 flex flex-col gap-1 p-2 sm:p-4 pt-3" data-cy="side-nav-container">
        {headerTitle && (
          <div className="text-sm font-medium text-slate-500 mb-2">
            {headerTitle}
          </div>
        )}
        {navLinks.map((link, index) => {
          const isActive = isNavlinkActive(link);
          return (
            <button key={link.route}
              className={cn("settings-side-nav-btn", isActive && "settings-side-nav-btn-active")}
              onClick={() => handleNavigate(link.route)}
            >
              <span className={cn("w-3 sm:w-6", showNumbering ? "inline-block" : "sm:hidden inline-block")}>{index + 1}.</span>
              <span className="hidden sm:inline">
                {link.text}
              </span>
            </button>
          );
        })}
        {
          advancedOptionsNavLinks &&
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="item-1">
                <AccordionTrigger className="text-xs font-normal text-gray-400 px-2 sm:px-0">
                  <span className="hidden sm:inline pl-2">
                    {t("sitenavigationmenu.advancedoptions")}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="flex flex-col gap-1">
                  {advancedOptionsNavLinks.map((link, index) => {
                    const isActive = isNavlinkActive(link);

                    return (
                      <button key={link.route}
                        className={cn("settings-side-nav-btn", isActive && "settings-side-nav-btn-active")}
                        onClick={() => handleNavigate(link.route)}
                      >
                        <span className="w-3 sm:w-6 inline-block">{index + 1}.</span>
                        <span className="hidden sm:inline">
                          {link.text}
                        </span>
                      </button>
                    );
                  })}
                </AccordionContent>
              </AccordionItem>
            </Accordion>
        }
      </nav>
    </div>
  );
}

export default SideNavigationMenu;
